import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Bell } from '@phosphor-icons/react';

const EmailChip = ({ email, busy, onUnsubscribe }) => (
  <Badge
    className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs gap-1 cursor-pointer"
    onClick={() => !busy && onUnsubscribe(email)}
    onKeyDown={(ev) => {
      if ((ev.key === 'Enter' || ev.key === ' ') && !busy) {
        ev.preventDefault();
        onUnsubscribe(email);
      }
    }}
    role="button"
    tabIndex={0}
    aria-label={`Remove ${email} from notifications`}
    data-testid={`odm-detail-notify-chip-${email}`}
    title={`Click to remove ${email}`}
  >
    {email}
    <span className="text-indigo-400 font-bold">×</span>
  </Badge>
);

const NotifyMeCard = ({ batchId, notifyEmails, currentUserEmail, onChange }) => {
  const [emailInput, setEmailInput] = useState('');
  const [busy, setBusy] = useState(false);
  const list = notifyEmails || [];
  const userSubscribed = currentUserEmail && list.includes(currentUserEmail.toLowerCase());

  const subscribe = async (emails) => {
    if (!emails.length) return;
    setBusy(true);
    try {
      const res = await api.post(
        `/odm-sftp/submissions/${encodeURIComponent(batchId)}/notify`,
        { emails }
      );
      onChange(res.data.notify_emails || []);
      const added = res.data.added || [];
      if (added.length) toast.success(`Subscribed: ${added.join(', ')}`);
      else toast.info('Already subscribed');
      setEmailInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async (email) => {
    setBusy(true);
    try {
      const res = await api.delete(
        `/odm-sftp/submissions/${encodeURIComponent(batchId)}/notify/${encodeURIComponent(email)}`
      );
      onChange(res.data.notify_emails || []);
      toast.success(`Unsubscribed: ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unsubscribe');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitInput = (e) => {
    e.preventDefault();
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    subscribe([trimmed]);
  };

  return (
    <Card data-testid="odm-detail-notify-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell size={16} className="text-indigo-600" weight="duotone" />
          Email me when ODM acknowledges this batch
        </CardTitle>
        <CardDescription className="text-xs">
          Receive a notification when ODM returns a TA1 (envelope), 999 (functional), 277CA, or 835 for this submission.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {currentUserEmail && (
          <Button
            size="sm"
            onClick={() =>
              userSubscribed ? unsubscribe(currentUserEmail) : subscribe([currentUserEmail])
            }
            disabled={busy}
            variant={userSubscribed ? 'outline' : 'default'}
            data-testid="odm-detail-notify-self-btn"
            className={userSubscribed ? '' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}
          >
            <Bell size={14} className="mr-1" />
            {userSubscribed
              ? `Unsubscribe ${currentUserEmail}`
              : `Notify me at ${currentUserEmail}`}
          </Button>
        )}

        <form onSubmit={handleSubmitInput} className="flex gap-2">
          <Input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Add another email…"
            disabled={busy}
            data-testid="odm-detail-notify-input"
            className="text-sm"
          />
          <Button
            type="submit"
            size="sm"
            disabled={busy || !emailInput.trim()}
            data-testid="odm-detail-notify-add-btn"
          >
            Add
          </Button>
        </form>

        {list.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
              Currently subscribed ({list.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {list.map((e) => (
                <EmailChip key={e} email={e} busy={busy} onUnsubscribe={unsubscribe} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotifyMeCard;
